import { Component, computed, inject, OnDestroy, OnInit, signal, Signal, untracked, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EMediaProvider, EMediaType } from 'app/core/enums/media-type.enum';
import { FileHelper } from 'app/core/helpers/file.helper';
import { HashHelper } from 'app/core/helpers/hash.helper';
import { IUser } from 'app/core/models/user/user';

import { Subscription } from 'rxjs';
import { LoadingComponent, ModalComponent } from '@kiwid-app/kiwid-ui-lib-angular';
import { KVideoComponent } from '@kiwid-app/kiwid-ui-lib-angular/k-video';
import { IMediaParameters } from 'app/core/models/media/media';
import heic2any from 'heic2any';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-modal-preview',
    templateUrl: './modal-preview.component.html',
    styleUrls: ['./modal-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [LoadingComponent, KVideoComponent, MatIconModule],
})
export class ModalPreviewComponent implements OnInit, OnDestroy {
    private readonly domSanitizer = inject(DomSanitizer);

    public dialog: Signal<MatDialogRef<ModalComponent, string>>;
    public previewMedia = signal<IMediaPreview[]>([]);
    public orderId: Signal<string>;
    public user: Signal<IUser>;
    public translate: WritableSignal<any>;
    protected readonly EMediaType = signal(EMediaType);
    // protected readonly mediaSelected = signal<IMediaPreview>(null);
    private readonly subscriptions = new Subscription();
    protected readonly renderMedias = computed(() => {
        const medias = this.previewMedia();
        const currentIndex = this.currentIndex();

        return medias.map((media, index) => {
            const url = signal(media.url);
            const isHeic = media.type === EMediaType.IMAGE && FileHelper.isHeic(media.file.name);
            const loading = signal(isHeic && !media.isConverted);

            if (isHeic && !media.isConverted) {
                untracked(() => {
                    loading.set(true);
                    url.set(null);

                    heic2any({
                        blob: media.file,
                        toType: 'image/jpeg',
                        quality: 1,
                    })
                        .then((convertedBlob) => {
                            if (convertedBlob) {
                                const file = new File([convertedBlob as Blob], media.file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });

                                url.set(this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file)));

                                loading.set(false);

                                this.previewMedia.update((prev) =>
                                    prev.map((m) => {
                                        if (m.id === media.id) {
                                            return {
                                                ...m,
                                                file: file,
                                                url: this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file)),
                                                isConverted: true,
                                            };
                                        }
                                        return m;
                                    }),
                                );
                            }
                        })
                        .then((error) => {
                            loading.set(false);
                        });
                });
            }

            return {
                ...media,
                opacity: index === currentIndex ? 100 : 30,
                isImage: media.type === EMediaType.IMAGE,
                isAudio: media.type === EMediaType.AUDIO,
                isVideo: media.type === EMediaType.VIDEO,
                isRaw: media.type === EMediaType.RAW,
                isLoading: loading,
                mediaUrl: url,
                actions: {
                    click: () => {
                        this.currentIndex.set(index);
                    },
                    remove: () => {
                        if (index === currentIndex && this.previewMedia().length > 1) {
                            this.currentIndex.set(0);
                        }
                        this.previewMedia.update((current) => current.filter((itemFilter) => itemFilter.id !== media.id));
                    },
                },
            };
        });
    });
    protected readonly currentIndex = signal(0);
    protected readonly mediaSelected = computed(() => {
        const index = this.currentIndex();
        const medias = this.renderMedias();

        if (index < 0 || index >= medias.length) {
            return null;
        }

        return medias[index];
    });

    ngOnInit(): void {}

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    addFile(input: HTMLInputElement): void {
        const previewMedia: IMediaPreview[] = [];
        const files = input.files as FileList;

        if (files.length + this.previewMedia().length > 15) {
            input.value = null;
            this.dialog().componentInstance.showToastr({
                type: 'error',
                message: this.translate()?.TOASTR?.MAX_FILE?.ERROR?.MESSAGE,
            });
            return;
        }

        for (let iFile = 0; iFile < files.length; iFile++) {
            const file = files.item(iFile);

            previewMedia.push({
                id: HashHelper.uuidv4(),
                file: file as File,
                type: FileHelper.getMediaTypeByName(file.name),
                provider: FileHelper.getProviderByName(file.name),
                progress: 0,
                url: this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file)),
            });
        }
        input.value = null;

        this.previewMedia.update((prev) => [...prev, ...previewMedia]);

        if (this.previewMedia().length === 1) {
            this.currentIndex.set(0);
        }
    }
}

export interface IMediaPreview {
    path?: string;
    providerId?: string;
    parameters?: IMediaParameters;
    url: SafeUrl;
    file: File;
    type: EMediaType;
    provider: EMediaProvider;
    progress: number;
    isConverted?: boolean;
    id: string;
}
